import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { map, startWith, switchMap } from 'rxjs/operators';
import { TrialsQuery } from '@models/trials/trials.query';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
  ChartDataSets,
  ChartLegendLabelOptions,
  ChartLegendOptions,
  ChartOptions,
  ChartScales,
  ChartTooltipItem,
  ChartType,
  GridLineOptions,
} from 'chart.js';
import { Color } from 'ng2-charts/lib/color';
import { Label } from 'ng2-charts';
import {
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  ExcelExportParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GetQuickFilterTextParams,
  ValueFormatterParams,
  RowClassParams,
} from '@ag-grid-community/core';
import { RequireSome, Utils } from '@services/utils';
import { LaunchDarklyService } from '@services/launch-darkly.service';
import { AuthQuery } from '@models/auth/auth.query';
import { AgPulseMinusComponent } from '@components/ag-actions/ag-pulse-minus.component';
import { EntityType, EventType, ExpenseType, GqlService, Trial } from '@services/gql.service';
import { OverlayService } from '@services/overlay.service';
import { Router } from '@angular/router';

import { round } from 'lodash-es';
import { AgCellWrapperComponent } from '@components/ag-cell-wrapper/ag-cell-wrapper.component';
import { AuthService } from '@models/auth/auth.service';
import { MainQuery } from '../../layouts/main-layout/state/main.query';
import { MainStore } from '../../layouts/main-layout/state/main.store';
import { PortfolioStatusComponent } from './portfolio-status/portfolio-status.component';
import { PortfolioCompleteComponent } from './portfolio-complete/portfolio-complete.component';
import { NewTrialDialogComponent } from '../../layouts/main-layout/new-trial-dialog/new-trial-dialog.component';
import { MainService } from '../../layouts/main-layout/state/main.service';
import { ROUTING_PATH } from '../../app-routing-path.const';
import { TableConstants } from '../../constants/table.constants';
import { MessagesConstants } from '@constants/messages.constants';
import { EventService } from '@services/event.service';

export interface PortfolioGridData {
  type: string;
  name: string;
  status: string;
  cmwp: number;
  clre: number;
  base: number;
  var_cost: number;
  percent: string;
  wpltd: number;
  complete: number;
  invoiced: number;
}

@UntilDestroy()
@Component({
  selector: 'aux-portfolio-dashboard',
  templateUrl: './portfolio-dashboard.component.html',
  styles: [
    `
      ::ng-deep main {
        background-color: var(--aux-gray-light);
      }

      ::ng-deep .portfolio-grid .ag-center-cols-clipper {
        background-color: var(--aux-gray-light);
      }

      ::ng-deep .portfolio-grid .ag-row {
        background-color: var(--aux-gray-light);
      }

      ::ng-deep .portfolio-grid .ag-cell-value {
        font-size: 12.25px;
      }

      ::ng-deep .portfolio-grid .ag-header-container {
        background-color: var(--aux-gray-light);
      }

      ::ng-deep .portfolio-grid .ag-floating-bottom {
        border-top: 0px solid var(--aux-gray-light);
      }

      ::ng-deep .trial-select.portfolio:not(.filled) .ng-placeholder {
        display: block !important;
      }

      ::ng-deep .trial-select .ng-select-container {
        @apply bg-transparent border-none h-full;
      }

      ::ng-deep .trial-select.ng-select .ng-select-container .ng-value-container {
        @apply pl-5 font-bold pt-4;
      }

      ::ng-deep .trial-select .ng-input {
        top: 22px !important;
        left: 8px !important;
      }

      ::ng-deep .trial-name.aux-link {
        color: var(--aux-blue);
        cursor: pointer;
        font-weight: normal;
      }

      ::ng-deep .ag-floating-bottom-container .ag-cell {
        font-size: 14px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortfolioDashboardComponent implements OnDestroy {
  alreadyWentToBudgetPage = false;

  loadingRefreshPortfolioDashboard$ = new BehaviorSubject(false);

  private PERCENTAGE_COLS = ['complete', 'var_percent'];

  excelOptions: ExcelExportParams = {
    sheetName: 'Dashboard',
    fileName: 'auxilius-dashboard.xlsx',
    processCellCallback: (cel) => {
      const colId = cel.column.getColId();

      if (this.PERCENTAGE_COLS.indexOf(colId) !== -1 && cel.value) {
        return cel.value / 100;
      }
      return cel.value;
    },
  };

  cardUsers$ = this.trialUserQuery.selectAll().pipe(
    map((users) => {
      const userId = this.authQuery.getValue().trial_id;
      const newUser = users.filter((user) => {
        // todo: remove this when we get superAdmin flag
        return user.id === userId ? user : null;
      });

      console.log(newUser);
      return newUser;
    })
  );

  trials$!: Observable<
    (
      | (Omit<RequireSome<Partial<Trial>, 'id'>, 'sponsor_organization'> & {
          sponsor_organization: { id: string; name: string };
        })
      | { id: string; short_name: string; onboarding_complete: boolean }
    )[]
  >;

  loading$ = new BehaviorSubject(false);

  priceClass = [
    'ag-cell-align-right',
    'tabular-nums',
    'budget-cost',
    'text-aux-black',
    'justify-end',
    'text-sm',
  ];

  lineChart!: {
    type: ChartType;
    options: ChartOptions;
    datasets: ChartDataSets[];
    colors: Color[];
    labels: Label[];
  };

  defaultColumns: (ColDef | ColGroupDef)[] = [
    {
      headerName: 'Current',
      headerClass: 'ag-header-align-center text-aux-black',
      minWidth: 130,
      children: [
        {
          headerName: 'Trial',
          headerClass: 'ag-header-align-center font-bold',
          cellClass: (value) => {
            if (value.data.name === 'Totals') {
              return ['font-bold text-aux-black'];
            }
            return ['trial-name', 'aux-link', '!text-base'];
          },
          cellStyle: { 'text-align': 'start' },
          minWidth: 140,
          field: 'name',
          sort: 'asc',
          onCellClicked: async (event: CellClickedEvent) => {
            if (event.data.name === 'Totals') {
              return null;
            }
            await this.onTrialSelect(event.data.id, false);
            this.route.navigate([`/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.INDEX}`]);
            return event;
          },
        },
        {
          headerName: 'Current Month Status',
          headerClass: 'ag-header-align-center font-bold',
          cellClass: '!justify-start',
          cellStyle: { height: '100%' },
          field: 'status',
          suppressMenu: true,
          minWidth: 180,
          cellRenderer: PortfolioStatusComponent,
          onCellClicked: async (event: CellClickedEvent) => {
            await this.onTrialSelect(event.data.id, false);
            this.route.navigate([
              `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.QUARTER_CLOSE}`,
            ]);
          },
          getQuickFilterText: (params: GetQuickFilterTextParams) => {
            switch (params.value as string) {
              case 'Open':
                return 'Open';
              case 'Closed':
                return 'Closed';
              default:
                return Utils.zeroHyphen;
            }
          },
        },
        {
          headerName: 'Current Month WP',
          headerClass: 'ag-header-align-center font-bold',
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          field: 'cmwp',
          minWidth: 100,
          suppressMenu: true,
          valueFormatter: this.agCurrencyFormatter,
        },
      ],
    },
    TableConstants.SPACER_COLUMN,
    {
      headerName: 'Budget',
      headerClass: ['ag-header-align-center', 'text-aux-black'],
      children: [
        {
          headerName: 'Current (LRE)',
          headerClass: 'ag-header-align-center font-bold',
          field: 'clre',
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          minWidth: 100,
          suppressMenu: true,
          valueFormatter: this.agCurrencyFormatter,
        },
        {
          headerName: 'Baseline',
          headerClass: 'ag-header-align-center font-bold',
          field: 'base',
          minWidth: 100,
          suppressMenu: true,
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          valueFormatter: this.agCurrencyFormatter,
        },
        {
          headerName: 'Var ($)',
          headerClass: 'ag-header-align-center font-bold',
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          field: 'var_cost',
          colId: 'var_cost',
          minWidth: 150,
          suppressMenu: true,
          cellRenderer: AgPulseMinusComponent,
        },
        {
          headerName: 'Var (%)',
          headerClass: 'ag-header-align-center font-bold',
          cellClass: ['percent', TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT, '!text-base'],
          field: 'percent',
          colId: 'var_percent',
          minWidth: 80,
          suppressMenu: true,
          cellRenderer: AgPulseMinusComponent,
        },
      ],
    },
    TableConstants.SPACER_COLUMN,
    {
      headerName: 'Work Performed',
      headerClass: ['ag-header-align-center', 'text-aux-black'],
      children: [
        {
          headerName: 'WP LTD',
          headerClass: 'ag-header-align-center font-bold',
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          field: 'wpltd',
          minWidth: 100,
          suppressMenu: true,
          valueFormatter: this.agCurrencyFormatter,
        },
        {
          headerName: '% Complete',
          headerClass: 'ag-header-align-center font-bold',
          field: 'complete',
          colId: 'complete',
          cellClass: ['items-center', 'complete-percent', '!text-base'],
          suppressMenu: true,
          cellRenderer: PortfolioCompleteComponent,
          minWidth: 150,
        },
        {
          headerName: 'Invoiced LTD',
          headerClass: 'ag-header-align-center font-bold',
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          field: 'invoiced',
          minWidth: 100,
          suppressMenu: true,
          valueFormatter: this.agCurrencyFormatter,
        },
      ],
    },
  ];

  gridOptions$ = new BehaviorSubject<GridOptions>({
    defaultColDef: {
      sortable: true,
      resizable: false,
      suppressMenu: true,
      suppressMovable: true,
      cellClass: (value) => {
        if (value.data.name === 'Totals') {
          return ['font-bold text-aux-black'];
        } /* This function is for Totals Row style format */
        if (value.colDef.field === 'status') {
          return [
            'items-center',
            'justify-start',
            'cursor-pointer',
            'ag-cell-align-right',
            'h-full',
          ];
        }
        return this.priceClass;
      },
      cellRenderer: AgCellWrapperComponent,
    },
    rowHeight: 35,
    headerHeight: 45,
    columnDefs: this.defaultColumns,
    rowClassRules: {
      'is-even': (params: RowClassParams) => {
        if (params.node.group || !params.node.parent) {
          return !!((params.node.rowIndex || 0) % 2);
        }
        const parentSiblings = params.node.parent.parent?.childrenAfterGroup;
        const parentIndexWithoutChildrens = (parentSiblings || []).findIndex(
          (p) => p.key === params.node.parent?.key
        );
        return !((parentIndexWithoutChildrens || 0) % 2);
      },
      'is-odd': (params: RowClassParams) => {
        if (params.node.group || !params.node.parent) {
          return !((params.node.rowIndex || 0) % 2);
        }
        const parentSiblings = params.node.parent.parent?.childrenAfterGroup;
        const parentIndexWithoutChildrens = (parentSiblings || []).findIndex(
          (p) => p.key === params.node.parent?.key
        );
        return !!((parentIndexWithoutChildrens || 0) % 2);
      },
    },
    getRowStyle: (params: RowClassParams): any => {
      if (params.node.rowPinned) {
        return { 'font-weight': 'bold', color: 'var(--text-aux-black)' };
      }
      return {};
    },
    excelStyles: [
      ...Utils.auxExcelStyle,
      {
        id: 'trial-name',
        dataType: 'String',
      },
      {
        id: 'var-percent',
        alignment: { horizontal: 'Right' },
        numberFormat: { format: Utils.excelPercentFormat },
      },
      {
        id: 'complete-percent',
        alignment: { horizontal: 'Right' },
        numberFormat: { format: Utils.excelPercentFormat },
      },
      {
        id: 'budget-units',
        alignment: { horizontal: 'Right' },
        numberFormat: { format: Utils.excelUnitsFormat },
      },
      {
        id: 'budget-cost',
        dataType: 'Number',
        numberFormat: { format: Utils.excelCostFormat },
      },
    ],
  } as GridOptions);

  section_portfolio_analytics_avg_month_close$: Observable<boolean>;

  gridAPI!: GridApi;

  gridData$ = new BehaviorSubject<PortfolioGridData[]>([]);

  cardValues = new BehaviorSubject<PortfolioGridData | undefined>(undefined);

  trialFc = new UntypedFormControl(null);

  showTaskSection$ = this.launchDarklyService.select$((flags) => flags.section_task_list);

  constructor(
    public trialsQuery: TrialsQuery,
    public mainQuery: MainQuery,
    private mainStore: MainStore,
    public authQuery: AuthQuery,
    private trialUserQuery: TrialsQuery,
    private gqlService: GqlService,
    public authService: AuthService,
    private launchDarklyService: LaunchDarklyService,
    private overlayService: OverlayService,
    private mainService: MainService,
    private route: Router,
    private eventService: EventService
  ) {
    this.authService
      .isAuthorized$({
        sysAdminsOnly: true,
      })
      .subscribe((hasPermission) => {
        this.trials$ = this.trialsQuery.selectAll().pipe(
          map((value) => {
            if (hasPermission) {
              const trialsWoutSelected = value.filter(
                (x) => x.id !== this.mainQuery.getValue().trialKey
              );
              // @ts-ignore
              const { id, short_name, onboarding_complete } = this.mainQuery.getSelectedTrial();
              return [
                { id: 'NEW_TRIAL', short_name: '+ Add New Trial', onboarding_complete: true },
                { id, short_name, onboarding_complete },
                ...trialsWoutSelected,
              ];
            }
            return value;
          })
        );
      });

    this.loading$.next(true);
    setTimeout(() => {
      this.mainStore.update({ fullPage: true, sideBar: false });
    }, 0);

    this.section_portfolio_analytics_avg_month_close$ = launchDarklyService.select$(
      (flags) => flags.section_portfolio_analytics_avg_month_close
    );

    this.eventService
      .select$(EventType.REFRESH_PORTFOLIO_DASHBOARD)
      .pipe(
        startWith(null),
        untilDestroyed(this),
        switchMap(() =>
          combineLatest([
            this.gqlService.listPortfolioSummaries$(),
            this.gqlService.listPortfolioExpenses$(),
          ])
        )
      )
      .subscribe(([{ success, data, errors }, expenses]) => {
        if (success && data) {
          const prevMonth = Utils.dateParse(this.getPrevMonth());

          this.gridData$.next(
            // this.gridData
            data.map((row) => {
              const currentMonth = Utils.dateParse(
                this.convertFormattedDateToDate(row.current_month || '')
              );
              // @ts-ignore
              const diff = prevMonth - currentMonth;
              const status = diff >= 0 ? 'Open' : 'Closed';

              let current_month_wp = row.current_month_work_performed || 0;
              let current_lre = 0;
              let wp_ltd = 0;
              const baseline = row.baseline_budget_estimate || 0;
              const invoiced = row.invoice_total || 0;
              row.current_budget_expenses.forEach((expense) => {
                switch (expense.expense_type) {
                  case ExpenseType.EXPENSE_ACCRUAL_ADJUSTED: {
                    current_month_wp =
                      current_month_wp > 0 ? current_month_wp : expense.amount || 0;
                    break;
                  }
                  case ExpenseType.EXPENSE_QUOTE: {
                    current_lre = expense.amount || 0;
                    break;
                  }
                  case ExpenseType.EXPENSE_WP: {
                    wp_ltd = expense.amount || 0;
                    break;
                  }
                  default:
                    break;
                }
              });
              const var_cost = round(current_lre - baseline, 2);
              const percent = baseline ? round((var_cost / baseline) * 100, 2) : 0;

              return {
                type: 'Active',
                name: row.trial_short_name,
                id: row.trial_id,
                status,
                cmwp: current_month_wp,
                clre: current_lre,
                base: baseline,
                var_cost,
                percent: percent.toFixed(2) !== '0.00' ? percent.toFixed(2) : '',
                wpltd: wp_ltd,
                complete: round((wp_ltd / current_lre) * 100, 2) || 0,
                invoiced,
              } as PortfolioGridData;
            })
          );
        } else {
          this.overlayService.error(errors);
        }

        if (expenses.success && expenses.data) {
          const obj: Record<string, Record<string, any>> = {};

          expenses.data.forEach((row) => {
            obj[row.period || ''] = obj[row.period || ''] || {};

            obj[row.period || ''][row.source || ''] = row.amount;
          });
          const periods = ['', ...Object.keys(obj)];
          const periodsFormatted = this.getLabels(periods);

          const today = new Date();
          const todayQY = `Q${Math.floor(today.getMonth() / 3) + 1} ${today.getFullYear()}`;

          this.lineChart = {
            datasets: [
              {
                data: this.runningSum(periods.map((period) => obj[period]?.CURRENT || 0)),
                yAxisID: 'CB',
                pointStyle: 'rectRounded',
                cubicInterpolationMode: 'monotone',
                label: 'Current (LRE)',
              },
              {
                data: this.runningSum(periods.map((period) => obj[period]?.BASE || 0)),
                pointStyle: 'rectRounded',
                cubicInterpolationMode: 'monotone',
                label: 'Baseline Budget',
              },
            ],
            options: {
              legend: {
                display: true,
                fontColor: 'text-aux-black',
                labels: {
                  usePointStyle: true,
                  padding: 10,
                  generateLabels: (chart) => {
                    // @ts-ignore
                    const labels = Chart.defaults?.global?.legend?.labels?.generateLabels(chart);
                    const labels2 = [];
                    for (const label of labels || []) {
                      labels2.push({
                        ...label,
                        fillStyle: label.strokeStyle,
                        pointStyle: 'line',
                      });
                    }
                    return labels2;
                  },
                } as ChartLegendLabelOptions,

                position: 'bottom',
              } as ChartLegendOptions,
              scales: {
                yAxes: [
                  {
                    id: 'CB',
                    position: 'right',
                    ticks: {
                      callback: (value) => {
                        if (value !== undefined && value && !Number.isNaN(value)) {
                          return Utils.currencyFormatter(value as number, {
                            style: 'currency',
                            currency: 'USD',
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 0,
                            notation: 'compact',
                          });
                        }
                        return '';
                      },
                    },
                  },
                ],
                xAxes: [
                  {
                    gridLines: {
                      color: periodsFormatted.map((pf) => (pf === todayQY ? '#095b95' : '#e5e5e5')),
                      lineWidth: periodsFormatted.map((pf) => (pf === todayQY ? 3 : 1)),
                    } as GridLineOptions,
                  },
                ],
              } as ChartScales,
              maintainAspectRatio: false,
              plugins: {
                datalabels: {
                  display: false,
                },
              },
              tooltips: {
                mode: 'x-axis',
                callbacks: {
                  label(tooltipItem: Chart.ChartTooltipItem, chartData: Chart.ChartData) {
                    let label = '';
                    if (
                      tooltipItem.datasetIndex !== null &&
                      tooltipItem.datasetIndex !== undefined &&
                      chartData.datasets
                    ) {
                      label = chartData.datasets[tooltipItem.datasetIndex].label || '';
                    }
                    if (tooltipItem.yLabel && typeof tooltipItem.yLabel === 'number') {
                      return `${label} ${Utils.currencyFormatter(tooltipItem.yLabel, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`;
                    }
                    return `${label} ${tooltipItem.yLabel || ''}`;
                  },
                  labelColor(tooltipItem: ChartTooltipItem) {
                    const color = tooltipItem.datasetIndex
                      ? 'rgba(8, 91, 149, 1)'
                      : 'rgba(19, 142, 231, 1)';
                    return { borderColor: color, backgroundColor: color };
                  },
                },
              },
            },
            colors: ['#138EE7', '#095B95', '#E3B506', '#BACAD0'].map((color) => ({
              borderColor: color,
              backgroundColor: 'rgba(0,0,0,0)',
              pointHoverBorderColor: color,
              pointRadius: 2,
              radius: 2,
              borderWidth: 4,
              pointHoverBorderWidth: 6,
              pointStyle: 'circle',
            })),
            type: 'line',
            labels: periodsFormatted,
          };
        } else {
          this.overlayService.error(expenses.errors);
        }

        this.loading$.next(false);
      });

    this.trialFc.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.onTrialSelect(value, true);
    });
  }

  percentageFormatter(val: number) {
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    let fVal = Utils.zeroHyphen;
    if (val) {
      fVal = `${formatter.format(val)}%`;
    }
    return fVal;
  }

  getLabels(data: string[]) {
    const labelSet = new Set<string>();
    for (const d of data) {
      if (d) {
        const label = this.transformPeriodStr(d);
        if (label) {
          labelSet.add(label);
        }
      } else {
        // Handle initial empty string (for first label)
        labelSet.add(d);
      }
    }
    const labelArr = Array.from(labelSet) as string[];
    // Sort by quarters
    return labelArr.sort((a, b) => {
      const year1 = parseInt(a.split(' ')?.[1], 10) || -1;
      const year2 = parseInt(b.split(' ')?.[1], 10) || -1;
      if (year1 !== year2) {
        return year1 > year2 ? 1 : -1;
      }
      return a > b ? 1 : -1;
    });
  }

  transformPeriodStr(period: string) {
    if (period) {
      const split = period.split('-');
      if (split.length > 1) {
        return `${split[1]} ${split[0]}`;
      }
    }
    return '';
  }

  runningSum(nums: number[]) {
    let total = 0;
    // eslint-disable-next-line no-return-assign
    return nums.map((num) => (total += num));
  }

  convertFormattedDateToDate(str: string) {
    const d = new Date(`03/${str.replace('-', '/')}`);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-01`;
  }

  async onTrialSelect(value: string, goToBudget: boolean) {
    if (value === 'NEW_TRIAL') {
      const ref = this.overlayService.open<string>({ content: NewTrialDialogComponent });

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ref.afterClosed$.subscribe((newTrialKey) => {
        if (newTrialKey.data) {
          this.trialFc.setValue(newTrialKey.data);
          this.alreadyWentToBudgetPage = true;
          this.goToBudget();
        } else {
          this.trialFc.reset();
        }
      });
    } else if (value) {
      await this.mainService.setTrial(value);
      await this.mainStore.setTrial();

      if (goToBudget && !this.alreadyWentToBudgetPage) {
        this.goToBudget();
      }
    }
  }

  goToBudget() {
    this.route.navigate([`/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.INDEX}`]);
  }

  getPrevMonth() {
    const today = new Date();
    const prev = Utils.addMonths(today, -1);
    const month = (prev.getMonth() + 1).toString().padStart(2, '0');
    const year = prev.getFullYear();
    return `${year}-${month}-01`;
  }

  ngOnDestroy() {
    this.mainStore.update({ fullPage: false, sideBar: true });
  }

  getDynamicExcelParams(): ExcelExportParams {
    const totalData = this.gridAPI.getPinnedBottomRow(0)?.data;

    return {
      shouldRowBeSkipped(params) {
        return params.node?.data?.name === 'Totals';
      },
      appendContent: [
        {
          cells: [
            {
              data: { value: 'Totals', type: 'String' },
              mergeAcross: 1,
              styleId: 'total_row_header',
            },
            {
              data: { value: `${totalData.cmwp}`, type: 'Number' },
              styleId: 'total_row',
            },
            {
              data: { value: `${totalData.clre}`, type: 'Number' },
              styleId: 'total_row',
            },
            {
              data: { value: `${totalData.base}`, type: 'Number' },
              styleId: 'total_row',
            },
            {
              data: { value: `${totalData.var_cost}`, type: 'Number' },
              styleId: 'total_row',
            },
            {
              data: { value: `${totalData.percent / 100}`, type: 'Number' },
              styleId: 'total_row_percent',
            },
            {
              data: { value: `${totalData.wpltd}`, type: 'Number' },
              styleId: 'total_row',
            },
            {
              data: { value: `${totalData.complete / 100}`, type: 'Number' },
              styleId: 'total_row_percent',
            },
            {
              data: { value: `${totalData.invoiced}`, type: 'Number' },
              styleId: 'total_row',
            },
          ],
        },
      ],
    };
  }

  onGridReady(e: GridReadyEvent) {
    this.gridAPI = e.api;

    this.gridData$.pipe(untilDestroyed(this)).subscribe(() => {
      const pinnedBottomData = { ...this.calculatePinnedBottomData(), name: 'Totals' };
      this.gridAPI.setPinnedBottomRowData([pinnedBottomData]);
    });
    this.autoSize();
  }

  calculatePinnedBottomData() {
    const columnsWithAggregation = [
      'cmwp',
      'clre',
      'base',
      'var_cost',
      'wpltd',
      'invoiced',
      'percent',
      'complete',
    ];

    const rData = this.gridData$.getValue().reduce(
      (acc, val) => {
        for (const key of columnsWithAggregation) {
          // @ts-ignore
          acc[key] += Number(val[key]);
        }
        return acc;
      },
      {
        cmwp: 0,
        clre: 0,
        base: 0,
        var_cost: 0,
        wpltd: 0,
        invoiced: 0,
        percent: '',
        complete: 0,
      }
    );
    this.cardValues.next(rData as PortfolioGridData);
    rData.percent = ((rData.var_cost / rData.base) * 100).toFixed(2);
    rData.complete = Math.round((rData.wpltd / rData.clre) * 100);
    return rData;
  }

  autoSize() {
    this.gridOptions$.getValue().api?.sizeColumnsToFit();
  }

  agCurrencyFormatter(val: ValueFormatterParams) {
    if (val.data) {
      if (val.data.expense_note && (val.colDef.field || '').indexOf('cost') >= 0) {
        return val.data.expense_note;
      }
    }

    if (val.value) {
      if (!Number.isNaN(val.value)) {
        return Utils.currencyFormatter(val.value);
      }
    }

    return Utils.zeroHyphen;
  }

  async triggerRefreshPortfolioDashboard() {
    this.loadingRefreshPortfolioDashboard$.next(true);
    await this.gqlService
      .processEvent$({
        type: EventType.REFRESH_PORTFOLIO_DASHBOARD,
        entity_type: EntityType.TRIAL,
        entity_id: '',
        payload: JSON.stringify({}),
      })
      .toPromise();
    this.overlayService.success(MessagesConstants.REFRESH_PORTFOLIO_DASHBOARD);
    this.loadingRefreshPortfolioDashboard$.next(false);
  }
}
